/**
 * Fait : décrit les documents des collections `comptes`, `invitations` et
 *   `sessions` — qui peut entrer dans l'atelier, sur quelle invitation, et
 *   comment une session le prouve. Chaque schéma est strict : une clé inconnue
 *   fait échouer la validation au lieu d'être retirée en silence.
 *
 * Ne fait pas : il ne hache rien — Argon2id et le calcul des empreintes
 *   vivent dans `apps/api/src/comptes/` — et ne décide d'aucun droit. Les droits d'un membre
 *   sur un projet vivent dans `projets.ts` : c'est là qu'est la loi du
 *   rattachement.
 */

import { z } from 'zod';

import { ROLES_DE_COMPTE, adresseElectronique, identifiant, nomDonne } from '../schemas.ts';

/**
 * Un adulte de la famille. L'inscription publique n'existe pas (D-005) :
 * un compte naît toujours d'une invitation.
 */
export const compte = z
  .object({
    adresseElectronique,
    nomAffiche: nomDonne,

    /**
     * Empreinte Argon2id du mot de passe. Le mot de passe en clair ne touche
     * jamais la base — cette chaîne est le seul témoin qu'il existe.
     */
    motDePasseHache: z.string().min(1),

    role: z.enum(ROLES_DE_COMPTE),

    /**
     * Un compte désactivé reste en base : ses messages et documents gardent un
     * auteur nommé. L'effacement réel passe par les marqueurs de suppression.
     */
    actif: z.boolean(),

    /**
     * LE COMPTE FONDATEUR — celui qui a fait naître la famille. Il ne peut
     * être ni fermé ni effacé depuis l'application, par personne, pas même
     * par lui-même : seule une intervention sur le serveur peut le retirer.
     *
     * Mots de Charles, le 2026-08-06 : « le seul compte qui ne peut pas être
     * détruit doit être le mien. Personne ne peut le détruire, même
     * moi-même, sauf si je le demande et qu'on le fait par logiciel. »
     *
     * Le champ est OPTIONNEL et ne vaut que `true` : un compte ordinaire ne
     * le porte pas du tout, plutôt que de porter un « faux » qu'il faudrait
     * poser partout et tenir à jour.
     */
    fondateur: z.literal(true).optional(),

    creeLe: z.date(),
    misAJourLe: z.date(),
  })
  .strict();

export type Compte = z.infer<typeof compte>;

/**
 * Une invitation à rejoindre l'atelier : à usage unique, hachée, expirante
 * (menace « invitation rejouée ou volée », critère 11).
 */
export const invitation = z
  .object({
    adresseInvitee: adresseElectronique,

    /**
     * Empreinte du jeton d'invitation. Le jeton en clair part dans le
     * courrier ; la base n'en garde que l'empreinte, invérifiable à rebours.
     */
    empreinteJeton: z.string().min(1),

    roleAttribue: z.enum(ROLES_DE_COMPTE),
    creeParId: identifiant,

    /**
     * L'adresse invitée, RECOPIÉE ici tant que l'invitation est vivante, et
     * retirée dès qu'elle est utilisée ou révoquée. Un index unique *sparse*
     * repose dessus : c'est ainsi que la base — et non une lecture qui peut
     * être doublée — garantit une seule invitation vivante par adresse.
     * (Un index PARTIEL sur « champ absent » n'existe pas dans MongoDB : la
     * présence d'un champ, elle, s'indexe.)
     */
    adresseVivante: adresseElectronique.optional(),

    creeLe: z.date(),
    expireLe: z.date(),

    /** Posée au premier usage : une invitation utilisée ne se rejoue pas. */
    utiliseeLe: z.date().optional(),

    /** Posée par un administrateur : révoquer vaut expirer tout de suite. */
    revoqueeLe: z.date().optional(),
  })
  .strict();

export type Invitation = z.infer<typeof invitation>;

/**
 * Une session ouverte. Le navigateur porte un cookie `HttpOnly` ; la base n'en
 * garde que l'empreinte, pour qu'une fuite de la base ne vole aucune session.
 */
export const session = z
  .object({
    compteId: identifiant,
    empreinteJeton: z.string().min(1),

    creeLe: z.date(),
    expireLe: z.date(),
    revoqueeLe: z.date().optional(),
  })
  .strict();

export type Session = z.infer<typeof session>;

/**
 * Une demande de nouveau mot de passe. Même forme qu'une invitation — jeton à
 * usage unique, empreinte seule en base, expiration — mais dans SA collection.
 *
 * POURQUOI PAS DANS LES INVITATIONS : la route d'inscription transforme toute
 * invitation vivante en COMPTE sur simple présentation du jeton. Un lien
 * d'oubli rangé là serait devenu une clé de création de compte.
 */
export const reinitialisation = z
  .object({
    compteId: identifiant,

    /** Le jeton en clair part dans le courrier ; la base n'en garde que ceci. */
    empreinteJeton: z.string().min(1),

    creeLe: z.date(),
    expireLe: z.date(),

    /** Posée au premier usage : un lien utilisé ne se rejoue pas. */
    utiliseeLe: z.date().optional(),
  })
  .strict();

export type Reinitialisation = z.infer<typeof reinitialisation>;
